import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, Steps, Typography, Space, message } from 'antd';
import { 
  SendOutlined, 
  RocketOutlined, 
  CheckCircleOutlined, 
  UserOutlined,
  NotificationOutlined
} from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';

const { Title, Text, Paragraph } = Typography;

const TelegramPromptModal = () => {
  const { user, updateTelegram } = useAuth();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [telegram, setTelegram] = useState('');
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà un pseudo telegram
    const hasPrompted = sessionStorage.getItem('telegram_prompted');
    
    if (user && !user.telegram_username && !hasPrompted) {
      const timer = setTimeout(() => {
        setVisible(true);
        sessionStorage.setItem('telegram_prompted', 'true');
      }, 5000); // 5 secondes après le login
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleSubmit = async () => {
    if (!telegram) {
      message.error(t('telegram_modal.step1_placeholder'));
      return;
    }

    setLoading(true);
    try {
      const result = await updateTelegram(telegram.startsWith('@') ? telegram : `@${telegram}`);

      if (result.success) {
        message.success(t('telegram_modal.success_msg'));
        setCurrentStep(1);
      } else {
        message.error(result.error || t('telegram_modal.error_msg'));
      }
    } catch (error) {
      message.error(t('telegram_modal.error_msg'));
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: t('telegram_modal.step1_title').split(':')[0].trim(),
      icon: <UserOutlined />,
    },
    {
      title: t('telegram_modal.step2_title').split(':')[0].trim(),
      icon: <RocketOutlined />,
    }
  ];

  return (
    <Modal
      open={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={500}
      className="premium-modal"
      centered
    >
      <div className="text-center py-4">
        <div className="w-16 h-16 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20">
          <NotificationOutlined style={{ fontSize: 28, color: '#6366f1' }} />
        </div>
        
        <Title level={3} className="!text-white font-outfit uppercase tracking-tight">
          {t('telegram_modal.title')}
        </Title>
        <Paragraph className="text-slate-400 mb-8">
          {t('telegram_modal.subtitle')}
        </Paragraph>

        <Steps 
          current={currentStep} 
          items={steps} 
          className="mb-10 telegram-steps"
          size="small"
        />

        {currentStep === 0 ? (
          <Space direction="vertical" className="w-full" size="large">
            <div className="text-left bg-slate-900/50 p-6 rounded-2xl border border-white/5">
              <Text className="text-xs font-black text-slate-500 uppercase tracking-widest ml-1 mb-4 block">
                {t('telegram_modal.step1_title')}
              </Text>
              <Paragraph className="text-slate-400 text-xs mb-4">
                {t('telegram_modal.step1_desc')}
              </Paragraph>
              <Input 
                prefix={<SendOutlined className="text-indigo-400" />}
                placeholder={t('telegram_modal.step1_placeholder')} 
                value={telegram}
                onChange={(e) => setTelegram(e.target.value)}
                className="terminal-input !py-3"
                onPressEnter={handleSubmit}
              />
            </div>
            
            <Button 
              type="primary" 
              icon={<CheckCircleOutlined />} 
              loading={loading}
              onClick={handleSubmit}
              className="btn-premium w-full !h-12 uppercase tracking-widest font-black"
            >
              {t('telegram_modal.step1_button')}
            </Button>
            
            <Button type="link" onClick={() => setVisible(false)} className="text-slate-500 hover:text-white">
              {t('telegram_modal.later')}
            </Button>
          </Space>
        ) : (
          <Space direction="vertical" className="w-full animate-fade-in" size="large">
            <div className="p-6 bg-slate-950/50 rounded-2xl border border-indigo-500/20">
              <Text className="text-xs font-black text-indigo-400 uppercase tracking-widest block mb-4">
                {t('telegram_modal.step2_title')}
              </Text>
              <Text className="text-white block mb-6 font-medium text-sm">
                {t('telegram_modal.step2_desc')}
              </Text>
              <Button 
                type="primary" 
                href="https://t.me/+votre_lien_prive" // Remplacer par le vrai lien
                target="_blank"
                icon={<SendOutlined />}
                className="bg-indigo-600 hover:bg-indigo-500 border-none w-full !h-14 font-black uppercase tracking-widest text-xs"
              >
                {t('telegram_modal.step2_button')}
              </Button>
            </div>
            
            <Button type="primary" onClick={() => setVisible(false)} className="w-full !h-12 glass-button uppercase tracking-widest font-black text-[10px]">
              {t('telegram_modal.step2_done')}
            </Button>
          </Space>
        )}
      </div>

      <style jsx="true">{`
        .premium-modal .ant-modal-content {
          background: linear-gradient(135deg, #0f172a 0%, #020617 100%);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 24px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
        .telegram-steps .ant-steps-item-title {
          color: #94a3b8 !important;
          font-weight: 700 !important;
          text-transform: uppercase !important;
          font-size: 10px !important;
          letter-spacing: 0.1em !important;
        }
        .telegram-steps .ant-steps-item-active .ant-steps-item-title {
          color: #fff !important;
        }
        .telegram-steps .ant-steps-item-icon {
          background: #1e293b !important;
          border-color: #334155 !important;
        }
        .telegram-steps .ant-steps-item-active .ant-steps-item-icon {
          background: #6366f1 !important;
          border-color: #6366f1 !important;
        }
      `}</style>
    </Modal>
  );
};

export default TelegramPromptModal;
